/**
 * Coming Soon — info-strip UI (v2.25)
 *
 * Replaces the standard MovieCard-based carousel for unreleased titles.
 * Text-first design: title, release date, where-it-releases, language, and
 * Notify button render INSTANTLY (before poster decodes). No <Link> wrapper
 * — unreleased titles have no useful detail page, so tiles are non-clickable
 * by design.
 *
 * Why this is faster than the previous rail:
 *   - No MovieCard (280 lines, multiple hooks, OTT-chip lookups, watchlist
 *     state, hover preview, navigation handlers) — none of that applies to
 *     unreleased titles.
 *   - Posters use a small 80px-wide thumbnail (TMDB w154), ~6KB each.
 *   - Text renders before poster decode (poster has explicit width/height
 *     so layout never shifts).
 *   - First 4 posters use `loading="eager"`, rest are `loading="lazy"`.
 */
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { devError } from "../lib/log";
import { Bell, Calendar, ChevronLeft, ChevronRight, Check, Tv2, Theater, PlayCircle, ArrowRight } from "lucide-react";
import { readSwrCache, writeSwrCache } from "../lib/swrCache";
import { toast } from "sonner";

const OTT_CHANNEL_STYLE = {
  "Netflix": { color: "text-red-300", bg: "bg-red-500/15", border: "border-red-500/30", icon: Tv2 },
  "Prime Video": { color: "text-sky-300", bg: "bg-sky-500/15", border: "border-sky-500/30", icon: Tv2 },
  "Amazon Prime Video": { color: "text-sky-300", bg: "bg-sky-500/15", border: "border-sky-500/30", icon: Tv2 },
  "Amazon MX Player": { color: "text-orange-200", bg: "bg-orange-500/15", border: "border-orange-500/30", icon: Tv2 },
  "JioHotstar": { color: "text-blue-200", bg: "bg-blue-600/15", border: "border-blue-500/30", icon: Tv2 },
  "Disney+ Hotstar": { color: "text-blue-200", bg: "bg-blue-600/15", border: "border-blue-500/30", icon: Tv2 },
  "SonyLIV": { color: "text-amber-200", bg: "bg-amber-500/15", border: "border-amber-500/30", icon: Tv2 },
  "JioCinema": { color: "text-rose-200", bg: "bg-rose-500/15", border: "border-rose-500/30", icon: Tv2 },
  "Apple TV+": { color: "text-zinc-200", bg: "bg-zinc-500/15", border: "border-zinc-500/30", icon: Tv2 },
  "HBO": { color: "text-purple-200", bg: "bg-purple-500/15", border: "border-purple-500/30", icon: Tv2 },
  "Zee5": { color: "text-violet-200", bg: "bg-violet-500/15", border: "border-violet-500/30", icon: Tv2 },
  "ZEE5": { color: "text-violet-200", bg: "bg-violet-500/15", border: "border-violet-500/30", icon: Tv2 },
  "MX Player": { color: "text-orange-200", bg: "bg-orange-500/15", border: "border-orange-500/30", icon: Tv2 },
  "Hulu": { color: "text-lime-200", bg: "bg-lime-500/15", border: "border-lime-500/30", icon: Tv2 },
  "AMC": { color: "text-red-200", bg: "bg-red-500/15", border: "border-red-500/30", icon: Tv2 },
  "Disney XD": { color: "text-blue-200", bg: "bg-blue-500/15", border: "border-blue-500/30", icon: Tv2 },
  "Theatre": { color: "text-amber-300", bg: "bg-amber-500/15", border: "border-amber-500/30", icon: Theater },
  "In Theatres": { color: "text-amber-300", bg: "bg-amber-500/15", border: "border-amber-500/30", icon: Theater },
  "Cinemas": { color: "text-amber-300", bg: "bg-amber-500/15", border: "border-amber-500/30", icon: Theater },
  "Streaming": { color: "text-emerald-200", bg: "bg-emerald-500/15", border: "border-emerald-500/30", icon: Tv2 },
};

function formatRelease(item) {
  const d = item.release_date;
  if (!d) return "TBD";
  try {
    const dt = new Date(d + "T00:00:00");
    const opts = { day: "numeric", month: "short" };
    const formatted = dt.toLocaleDateString("en-IN", opts);
    const days = item.days_until_release;
    if (typeof days === "number") {
      if (days <= 0) return `${formatted} · Today`;
      if (days === 1) return `${formatted} · Tomorrow`;
      if (days < 30) return `${formatted} · ${days}d`;
      if (days < 90) return `${formatted}`;
      return `${formatted}, ${dt.getFullYear()}`;
    }
    return formatted;
  } catch {
    return d;
  }
}

function LangBadge({ lang }) {
  const map = {
    hi: "Hindi", en: "English", ta: "Tamil", te: "Telugu",
    ml: "Malayalam", kn: "Kannada", bn: "Bengali", mr: "Marathi",
    pa: "Punjabi", gu: "Gujarati", ko: "Korean", ja: "Japanese",
    es: "Spanish", fr: "French",
  };
  const label = map[lang] || (lang || "").toUpperCase();
  if (!label) return null;
  return (
    <span className="text-[10px] text-white/55 leading-none uppercase tracking-wider">
      {label}
    </span>
  );
}

function ChannelChip({ channel }) {
  if (!channel) return null;
  const style = OTT_CHANNEL_STYLE[channel] || OTT_CHANNEL_STYLE["Streaming"];
  const Icon = style.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${style.color} ${style.bg} ${style.border}`}
      data-testid={`channel-${channel.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <Icon className="w-2.5 h-2.5" />
      {channel}
    </span>
  );
}

// Normalize poster URL — handles both full CDN URLs and raw TMDB paths ("/abc.jpg")
function normalizePoster(p, size = "w185") {
  if (!p) return null;
  if (p.startsWith("http")) return p.replace(/\/w\d+\//g, `/${size}/`);
  return `https://image.tmdb.org/t/p/${size}${p}`;
}

function UpcomingTile({ item, eager, isNotified, onNotify, busy }) {
  const release  = formatRelease(item);
  const channel  = item.platform_name || item.release_channel;
  const days     = item.days_until_release;
  const isFamous = item.is_famous;

  const countdownLabel = typeof days === "number"
    ? days <= 0  ? "Today"
    : days === 1 ? "Tomorrow"
    : `${days}d`
    : null;

  return (
    <div
      className={`shrink-0 w-[152px] sm:w-[165px] flex flex-col rounded-xl overflow-hidden transition-all duration-200 bg-[#111] ${
        isFamous
          ? "border border-amber-400/60 shadow-[0_0_12px_rgba(251,191,36,0.25)] hover:border-amber-400/90"
          : "border border-white/10 hover:border-amber-400/50"
      }`}
      data-testid={`upcoming-tile-${item.tmdb_id}`}
    >
      {/* ── Poster (portrait 2:3) ── */}
      <div className="relative w-full aspect-[2/3] bg-zinc-900 overflow-hidden">
        {/* Fallback title text */}
        <div className="absolute inset-0 flex items-center justify-center px-2">
          <p className="text-white text-[9px] font-bold leading-tight text-center line-clamp-4 tracking-wide">
            {item.title}
          </p>
        </div>
        {item.poster_path && (
          <img
            src={normalizePoster(item.poster_path, "w342")}
            alt={item.title}
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={eager ? "high" : "low"}
            onError={e => { e.target.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover z-10"
          />
        )}
        {/* Countdown pill — top-left */}
        {countdownLabel && (
          <div
            className="absolute top-2 left-2 z-20 bg-black/75 backdrop-blur-sm border border-amber-400/50 text-amber-300 text-[10px] font-extrabold tracking-wider px-2 py-0.5 rounded-full"
            data-testid="upcoming-countdown-badge"
          >
            {countdownLabel}
          </div>
        )}
        {/* Famous / Featured ribbon */}
        {isFamous && (
          <div className="absolute top-2 right-2 z-20 bg-amber-400 text-black text-[8px] font-extrabold tracking-wider px-1.5 py-0.5 rounded shadow-lg uppercase">
            ★
          </div>
        )}
        {/* Notify bell — bottom-right of poster */}
        <button
          onClick={e => { e.stopPropagation(); onNotify(); }}
          disabled={busy}
          data-testid={`notify-btn-${item.tmdb_id}`}
          aria-label={isNotified ? "Notified" : "Notify me"}
          className={`absolute bottom-2 right-2 z-20 p-1.5 rounded-full border transition ${
            isNotified
              ? "bg-amber-500/30 border-amber-400/60 text-amber-300"
              : "bg-black/60 border-white/20 text-white/60 hover:border-amber-400/50 hover:text-amber-300"
          }`}
        >
          <Bell className="w-3 h-3" />
        </button>
        {/* Bottom gradient for text readability */}
        <div className="absolute bottom-0 inset-x-0 h-12 bg-gradient-to-t from-black/70 to-transparent z-[15]" />
      </div>

      {/* ── Text details ── */}
      <div className="px-2.5 pt-2 pb-2.5 flex flex-col gap-1">
        <h3
          className="text-[12px] font-bold leading-tight line-clamp-2 text-white/90"
          data-testid="upcoming-title"
        >
          {item.title}
        </h3>
        <p className="text-[10px] text-amber-300/80 font-semibold leading-none mt-0.5">
          {release}
        </p>
        <div className="mt-1">
          <ChannelChip channel={channel} />
        </div>
      </div>
    </div>
  );
}


export default function HomeAnticipated({ initialItems }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    if (Array.isArray(initialItems) && initialItems.length > 0) return initialItems;
    return readSwrCache("coming-soon-india") || [];
  });
  const [notifiedSet, setNotifiedSet] = useState(new Set());
  const [busy, setBusy] = useState({});
  const hasFreshData = useRef(false);  // prevents stale bundle from overwriting fresh /coming-soon
  // v2.25 — Restore the popularity / sequence sort toggle from the previous
  // MovieCard-based rail. "sequence" = closest release first (backend default);
  // "popular" = popularity desc (anticipation buzz).
  const [sortMode, setSortMode] = useState("sequence");
  const scrollRef = useRef(null);

  // Sync initialItems from parent ONLY before we have our own fresh fetch.
  useEffect(() => {
    if (hasFreshData.current) return;  // don't overwrite our fresh /coming-soon data
    if (Array.isArray(initialItems) && initialItems.length > 0) {
      setItems(initialItems);
      writeSwrCache("coming-soon-india", initialItems);
    }
  }, [initialItems]);

  useEffect(() => {
    let alive = true;
    // Always refresh from /coming-soon on mount — even when initialItems was
    // provided from the home bundle. Bundle data can be stale (old cache key)
    // while /coming-soon reflects the latest curated sheet entries.
    (async () => {
      try {
        const r = await api.get("/coming-soon");
        const arr = r.data?.results || [];
        if (alive && arr.length) {
          hasFreshData.current = true;
          setItems(arr);
          writeSwrCache("coming-soon-india", arr);
        }
      } catch (e) { devError(e); }
    })();
    return () => { alive = false; };
  }, []);

  // Hydrate notified set so we render the right button state.
  useEffect(() => {
    if (!user) { setNotifiedSet(new Set()); return; }
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/anticipated-india/my-notifications");
        const arr = r.data?.results || [];
        if (alive) {
          setNotifiedSet(new Set(arr.map((x) => `${x.media_type}-${x.tmdb_id}`)));
        }
      } catch (e) { devError(e); }
    })();
    return () => { alive = false; };
  }, [user]);

  const handleNotify = async (item) => {
    const key = `${item.media_type}-${item.tmdb_id}`;
    if (busy[key]) return;
    if (!user) {
      toast.error("Sign in to get release notifications");
      return;
    }
    setBusy((b) => ({ ...b, [key]: true }));
    // Optimistic — mark notified immediately
    setNotifiedSet((s) => new Set([...s, key]));
    try {
      await api.post("/anticipated-india/subscribe", {
        tmdb_id: item.tmdb_id,
        media_type: item.media_type,
        title: item.title,
        release_date: item.release_date,
      });
      toast.success(`We'll notify you when "${item.title}" releases`);
    } catch (e) {
      // Roll back optimistic state
      setNotifiedSet((s) => { const ns = new Set(s); ns.delete(key); return ns; });
      toast.error("Couldn't save — try again");
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 420, behavior: "smooth" });
  };

  // Snap back to the start when sort changes so user sees the new top tile.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ left: 0, behavior: "smooth" });
  }, [sortMode]);

  // Apply client-side sort.
  const visible = (() => {
    if (!items) return items;
    if (sortMode === "popular") {
      return [...items].sort((a, b) => {
        // Important (is_famous) titles float to the top
        const aFamous = a.is_famous ? 1 : 0;
        const bFamous = b.is_famous ? 1 : 0;
        if (bFamous !== aFamous) return bFamous - aFamous;
        // Within each group: nearest release date first
        const aDays = a.days_until_release ?? 9999;
        const bDays = b.days_until_release ?? 9999;
        return aDays - bDays;
      });
    }
    // "sequence" = backend's default (release date asc) — no resort needed.
    return items;
  })();

  // Skeleton during cold load
  if (!visible || visible.length === 0) {
    return (
      <section className="px-6 lg:px-10 mt-10" data-testid="home-anticipated-skeleton">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-1 h-7 bg-amber-400/60 rounded-full" />
            <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-400/80" />
              <span>Coming Soon</span>
            </h2>
          </div>
          <div className="flex gap-3 overflow-hidden pb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="shrink-0 w-[300px] sm:w-[340px] h-[174px] rounded-xl fm-skel" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 lg:px-10 mt-10" data-testid="home-anticipated">
      <div className="max-w-[1440px] mx-auto">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-1 h-7 bg-amber-400/60 rounded-full" />
            <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-400/80" />
              <span>Coming Soon</span>
              <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold ml-1">
                · {visible.length}
              </span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {/* See All link */}
            <Link
              to="/list/anticipated-india"
              data-testid="coming-soon-see-all"
              className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300/70 hover:text-amber-300 transition"
            >
              See all <ArrowRight className="w-3 h-3" />
            </Link>
            {/* Sort toggle — popularity vs release sequence */}
            <div
              className="flex items-center bg-white/[0.04] border border-white/10 rounded-full p-0.5"
              data-testid="upcoming-sort-toggle"
            >
              <button
                onClick={() => setSortMode("sequence")}
                data-testid="upcoming-sort-sequence"
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide transition ${
                  sortMode === "sequence"
                    ? "bg-amber-500/25 text-amber-200"
                    : "text-white/55 hover:text-white/80"
                }`}
              >
                BY DATE
              </button>
              <button
                onClick={() => setSortMode("popular")}
                data-testid="upcoming-sort-popular"
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide transition ${
                  sortMode === "popular"
                    ? "bg-amber-500/25 text-amber-200"
                    : "text-white/55 hover:text-white/80"
                }`}
              >
                POPULAR
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => scroll(-1)}
                aria-label="Scroll left"
                data-testid="upcoming-scroll-left"
                className="p-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.10] border border-white/10 transition"
              >
                <ChevronLeft className="w-4 h-4 text-white/70" />
              </button>
              <button
                onClick={() => scroll(1)}
                aria-label="Scroll right"
                data-testid="upcoming-scroll-right"
                className="p-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.10] border border-white/10 transition"
              >
                <ChevronRight className="w-4 h-4 text-white/70" />
              </button>
            </div>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto pb-2 fm-scroll-hidden"
          data-testid="upcoming-strip"
        >
          {visible.map((item, idx) => {
            const key = `${item.media_type}-${item.tmdb_id}`;
            return (
              <UpcomingTile
                key={key}
                item={item}
                eager={idx < 4}
                isNotified={notifiedSet.has(key)}
                onNotify={() => handleNotify(item)}
                busy={!!busy[key]}
              />
            );
          })}
        </div>
        {/* Mobile "See all" footer */}
        <div className="mt-3 flex justify-end sm:hidden">
          <Link
            to="/list/anticipated-india"
            data-testid="coming-soon-see-all-mobile"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300/70 hover:text-amber-300 transition"
          >
            See all upcoming <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </section>
  );
}
